import type { ContestEntryApplicantFormValue } from "@/components/organisms/event/contestEntryApplicantInfoFormEventHandlers";
import type { CreateNolaPfContestEntryInput, CreateNolaPfContestExtraAnswer } from "@/lib/models/nolaPfContest";

/**
 * 確認画面の入力を POST /contest/entries のボディにする。
 * contestId は microCMS の contestId。penname は pennames の 1 件にする。
 * historyOfAwards は Step1 の入力値。未入力は空文字。
 * categoryId / workTitle / summary は Step2（応募作品の選択）の入力値。
 */
export function buildCreateContestEntryInput(input: {
  contestId: string;
  novelId: string;
  categoryId: string;
  workTitle: string;
  summary: string;
  applicant: ContestEntryApplicantFormValue;
  extraAnswers?: CreateNolaPfContestExtraAnswer[];
}): CreateNolaPfContestEntryInput {
  const body: CreateNolaPfContestEntryInput = {
    contestId: input.contestId,
    novelId: input.novelId,
    categoryId: input.categoryId,
    workTitle: input.workTitle,
    summary: input.summary,
    user: {
      name: input.applicant.name,
      nameKana: input.applicant.nameKana,
      pennames: [input.applicant.penname],
      email: input.applicant.email,
      phone: input.applicant.phone,
      gender: input.applicant.gender,
      birthDate: input.applicant.birthDate,
      historyOfAwards: input.applicant.historyOfAwards,
    },
  };
  if (input.extraAnswers && input.extraAnswers.length > 0) {
    body.extraAnswers = input.extraAnswers;
  }
  return body;
}

/** 応募 API のエラー文言を取り出す。RFC 9457 の detail を優先し、無ければ `{ message }`。 */
export function readContestEntryErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return null;
  }
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== "object") {
    return null;
  }
  const problemDetail = readProblemDetail(data);
  if (problemDetail) return problemDetail;

  if (!("message" in data)) return null;
  const { message } = data as { message?: unknown };
  return typeof message === "string" && message.length > 0 ? message : null;
}

/** RFC 9457 Problem Details の、この応答に特化した説明。 */
function readProblemDetail(data: object): string | null {
  if (!("type" in data) && !("status" in data) && !("errors" in data)) {
    return null;
  }
  if ("detail" in data && typeof data.detail === "string" && data.detail.length > 0) {
    return data.detail;
  }
  if ("errors" in data && Array.isArray(data.errors)) {
    const details = data.errors.flatMap((item) => {
      if (!item || typeof item !== "object" || !("detail" in item)) return [];
      const { detail } = item as { detail?: unknown };
      return typeof detail === "string" && detail.length > 0 ? [detail] : [];
    });
    if (details.length > 0) return details.join("\n");
  }
  return null;
}
